/**
 * The buyer's tier selection from the landing page, persisted across the
 * signup/verification detour. localStorage (not sessionStorage) on
 * purpose: the email-verification link opens a new tab, which would lose
 * per-tab storage.
 */

const STORAGE_KEY = "rawgallery.buyIntent";
const TTL_MS = 24 * 60 * 60 * 1000;

export interface BuyIntent {
  tierId: string;
  presetId: string | null;
  locale: string;
}

interface StoredBuyIntent extends BuyIntent {
  savedAt: number;
}

export function saveBuyIntent(intent: BuyIntent): void {
  try {
    const stored: StoredBuyIntent = { ...intent, savedAt: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Storage unavailable (private mode etc.) — checkout falls back to
    // the default tier.
  }
}

export function loadBuyIntent(): BuyIntent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as StoredBuyIntent;
    if (
      typeof stored.tierId !== "string" ||
      typeof stored.savedAt !== "number" ||
      Date.now() - stored.savedAt > TTL_MS
    ) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return {
      tierId: stored.tierId,
      presetId: typeof stored.presetId === "string" ? stored.presetId : null,
      locale: typeof stored.locale === "string" ? stored.locale : "cs",
    };
  } catch {
    return null;
  }
}

export function clearBuyIntent(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clean if storage is unavailable.
  }
}
